import { Link } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Bug,
  Copy,
  Flag,
  FlaskConical,
  MoreHorizontal,
  Pause,
  Play,
  RotateCcw,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ActorLabel } from '~/components/common/actor-label';
import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { TEMPORAL_NAMESPACE, temporalWorkflowUrl } from '~/constants/config/env';
import {
  BatchDebugPanel,
  type DebugIdEntry,
} from '~/features/catalog-operations/components/batch-detail/batch-debug-panel';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { useAuthStore } from '~/store/auth.store';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';
import { formatDateTime, formatDurationMs, formatRelative } from '~/utils/date';

import { isHiddenStage } from '../constants/hidden-stages';
import { useStageMetadata } from '../hooks/use-run';
import { useRunArchive } from '../hooks/use-run-archive';
import { useFunnelSummary } from '../hooks/use-run-funnel';
import { useRunLabels } from '../hooks/use-run-labels';
import { type RunNextAction, useRunNextStep } from '../hooks/use-run-next-step';
import { useRunToggle } from '../hooks/use-run-toggle';
import type { RunProgress, RunRead } from '../types/run.api.types';
import { batchCounts } from '../utils/batch-counts';
import { isMovingStatus } from '../utils/batches';
import { isRecentRun } from '../utils/batches-table';
import { catalogLabel } from '../utils/catalog-labels';
import { useCrossLinkSearch } from '../utils/nav-context';
import { formatCount, stageCount } from '../utils/run-format';
import { isRunLive, isRunStopped } from '../utils/run-status';
import { hasFailure } from '../utils/run-tabs';

import { BatchStatusBadge } from './batch-status';
import { StatusCell } from './batches-table-cells';
import { ConnectorMark } from './connector-mark';
import { HeaderIdsPopover } from './gate-handoff-ids';
import { NextWaitingBatchLink } from './next-waiting-batch-link';
import { type Crumb, PageBreadcrumbs } from './page-breadcrumbs';
import { PageHelpScope } from './page-help';
import { HelpPanel, HelpTerms } from './page-intro';
import { RunFactsActions } from './run-facts-actions';
import { DeliveryHelp } from './run-publishing-sections';
import { RunStateLine, RunStoppedPrimary } from './run-what-happened';

interface Props {
  runId: string;
  run: RunRead | undefined;
  progress: RunProgress | undefined;
  isLoading: boolean;
  /** The run's name from `useRunTitle` — the same string the breadcrumb shows. */
  title: string;
  experimentId: string | null;
  experimentName: string | null;
  breadcrumbs: Crumb[];
  /** The run tab on screen — the primary action never re-opens it (Q27). */
  activeTab: string;
  /** Rendered under the meta line (the phase strip). */
  children?: ReactNode;
}

/** First 8 chars of a long id — the full value stays one click away (copy). */
function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

/**
 * "How this batch works": the run's RESOLVED template — every stage it will run, in order,
 * ⚑ on the ones that gate for a decision, with a one-line description each. One place answers
 * "what does this flow actually do", instead of the four generic phases alone.
 */
function FlowStagesHelp({
  run,
  destination,
}: {
  run: RunRead | undefined;
  /** Resolved connector name, so DELIVERY reads "Publish to Google Merchant Center" here too. */
  destination: string | null;
}) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { stageLabel } = useRunLabels();
  // The backend's own per-stage description (B1), preferred over the local copy when it has one
  // for this stage; the stage NAME above always comes from this app's labels file, never here.
  const { data: stageMetadata } = useStageMetadata();
  const remoteDescription = (stage: string): string | null =>
    stageMetadata?.find((m) => m.stage === stage)?.description ?? null;
  const flow = run?.config?.flow;
  if (!flow || flow.stages.length === 0) return null;
  const gated = new Set(flow.gatedStages);
  return (
    <div className="flex flex-col gap-3 border-t pt-5">
      <div>
        <h3 className="text-foreground text-sm font-semibold">{t('header.help.flowTitle')}</h3>
        <p className="text-muted-foreground text-xs">{t('header.help.flowBody')}</p>
      </div>
      <ol className="flex flex-col gap-2.5">
        {/* Image check is hidden from the UI (hidden-stages.ts) — numbering skips it. */}
        {flow.stages
          .filter((stage) => !isHiddenStage(stage))
          .map((stage, i) => (
            <li key={stage} className="flex items-start gap-2 text-sm">
              <span className="text-muted-foreground w-4 shrink-0 text-right text-xs tabular-nums">
                {i + 1}.
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="text-foreground font-medium">
                  {stageLabel(stage, destination)}
                  {gated.has(stage) ? (
                    <Flag
                      aria-label={t('phaseTimeline.decisionPoint')}
                      className="text-warning ml-1 inline size-3 align-[-1px]"
                    />
                  ) : null}
                </span>
                <span className="text-muted-foreground text-xs leading-snug">
                  {remoteDescription(stage) ??
                    t(`header.help.stageDescriptions.${stage}`, {
                      defaultValue: t('header.help.stageDescriptions.generic'),
                    })}
                </span>
              </div>
            </li>
          ))}
      </ol>
    </div>
  );
}

/** The status band's tint: waiting amber, running blue, done green, a recent failure red. */
function bandTone(status: string, recent: boolean): string {
  if (status === 'AWAITING_GATE' || status === 'COST_GATE')
    return 'bg-warning-50 border-warning/20';
  if (status === 'COMPLETED') return 'bg-success-50 border-success/20';
  if (recent && (status === 'FAILED' || status === 'REJECTED' || status === 'TERMINATED')) {
    return 'bg-error-50 border-error/20';
  }
  if (isMovingStatus(status)) return 'bg-info-50 border-info/20';
  return 'bg-muted/40';
}

/**
 * The batch's header (guide part 2 §2.1): breadcrumb + ⓘ; the name; one muted facts line (store,
 * who started it and when, the product count, the experiment, Copy IDs) with the facts / settings
 * icons and `⋯` (pause, ids, archive, support) on the right; then a full-width status band in the
 * state's colour — the status, the one sentence on where the batch stands (an open gate: its name,
 * how long it has waited and since when) and the ONE primary action (Review →, Resume, Recover,
 * See delivery). Status reads `/progress` first because it polls.
 */
export function RunHeader({
  runId,
  run,
  progress,
  isLoading,
  title,
  experimentId,
  experimentName,
  breadcrumbs,
  activeTab,
  children,
}: Props) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const { stageLabel } = useRunLabels();
  const { toggle, isPending, pendingKind } = useRunToggle(runId);
  const { archive, unarchive, isPending: isArchivePending } = useRunArchive(runId);
  const next = useRunNextStep(run, progress, activeTab);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  // Support/debug entry — a super-admin-only troubleshooting aid, gated exactly like the legacy
  // batch-debug-panel: the `debugger-int` flag AND super admin, so it never reaches customers.
  const { isAllowed: isDebuggerAllowed } = useFeatureGate('debugger-int');
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const showDebug = isDebuggerAllowed && isSuperAdmin;
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  // R3-3 — the experiment chip and the "Decide in experiment" action both leave the batch; carry
  // `from` like every other cross-link out of it.
  const crossLinkSearch = useCrossLinkSearch();
  // The one product number (guide part 2 §2.3): products once curation counted them, else listings.
  const funnel = useFunnelSummary(runId).data;
  const productCount = batchCounts(
    funnel?.kind === 'ok' ? funnel.summary : null,
    progress,
  ).products;

  if (isLoading && !run && !progress) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-7 w-80" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const runStatus = progress?.runStatus ?? run?.status ?? '';
  const paused = progress?.paused ?? false;
  const live = isRunLive(runStatus);
  const connector = progress?.connectorCode ?? run?.connectorCode ?? null;
  const flow = progress?.flow ?? run?.flow ?? run?.config?.flow?.name ?? null;
  const startedAt = progress?.startedAt ?? run?.startedAt ?? null;
  const workflowId = progress?.workflowId ?? run?.workflowRef ?? null;
  const creator = run?.audit.createdBy ?? null;
  const createdByHandle = creator?.handle ?? progress?.createdBy ?? null;
  const trigger = run?.triggerSource ? catalogLabel(tCatalog, 'trigger', run.triggerSource) : null;
  const archived = !!run?.archivedAt;

  // Products: the same figures the phase strip's flow shows (Q9) — extract's size, then the
  // test arm when the run splits one.
  const stages = progress?.stages ?? [];
  const importStage = stages.find((s) => s.stage === 'IMPORT');
  const armStage = stages.find((s) => s.stage === 'ARM_ASSIGNMENT');
  const imported = progress?.funnel?.extracted ?? stageCount(importStage, 'products_succeeded');
  const testArm =
    armStage && armStage.status !== 'PENDING'
      ? stageCount(armStage, 'products_succeeded')
      : undefined;

  // Design round 2 §M-A — a chip per fact instead of a "·"-joined sentence. The destination gets
  // its bundled connector logo when one exists, else a plain Store mark; the person chip reuses
  // the one actor/user-name resolver every "who did this" field in this app goes through.
  // A scheduled run's creator is the worker's service actor ("catalog-worker"): the chip names
  // the schedule that fired it instead.
  const byActor = run?.scheduleName ? (
    <span className="inline-flex">{t('factsRail.bySchedule', { name: run.scheduleName })}</span>
  ) : creator?.actorId || createdByHandle ? (
    <ActorLabel
      actor={{
        id: creator?.actorId ?? null,
        displayName: createdByHandle ?? '',
        kind: creator?.kind ?? 'USER',
        email: null,
        avatarUrl: null,
        resolved: !!createdByHandle,
      }}
      showAvatar
      className="inline-flex"
    />
  ) : null;

  // One quiet line of facts under the name: store, who started it and when, products in, the
  // experiment it belongs to, then the ids popover — words, not a row of chips.
  const facts: ReactNode[] = [
    connector ? (
      <span
        key="connector"
        className="inline-flex items-center gap-1.5"
        title={flow ? catalogLabel(tCatalog, 'flow', flow) : undefined}
      >
        <ConnectorMark code={connector} />
        {catalogLabel(tCatalog, 'connector', connector)}
      </span>
    ) : null,
    startedAt ? (
      <span key="started" className="inline-flex flex-wrap items-center gap-1">
        {byActor ? (
          <>
            {t('header.line.startedBy')} <span className="text-foreground">{byActor}</span>
          </>
        ) : trigger ? (
          t('header.facts.via', { trigger })
        ) : null}
        <span title={formatDateTime(startedAt)}>{formatRelative(startedAt)}</span>
      </span>
    ) : (
      <span key="notStarted">{t('header.facts.notStarted')}</span>
    ),
    imported !== undefined ? (
      // Links to the batch's own product list — never a plain, inert fact.
      <Link
        key="imported"
        to="/catalog-enrichment/runs/$runId/products"
        params={{ runId }}
        className="text-primary focus-visible:ring-ring cursor-pointer rounded hover:underline focus-visible:ring-2 focus-visible:outline-none"
      >
        {productCount !== null
          ? testArm !== undefined
            ? t('header.meta.productsWithArm', {
                count: productCount,
                products: formatCount(productCount),
                testArm: formatCount(testArm),
              })
            : t('header.meta.products', {
                count: productCount,
                products: formatCount(productCount),
              })
          : t('header.meta.listings', { count: imported, listings: formatCount(imported) })}
      </Link>
    ) : null,
    experimentId ? (
      <Link
        key="experiment"
        to="/catalog-enrichment/experiments/$experimentId"
        params={{ experimentId }}
        search={crossLinkSearch}
        title={experimentName ?? undefined}
        className="text-exp-status-created inline-flex cursor-pointer items-center gap-1 hover:underline"
      >
        <FlaskConical aria-hidden="true" className="size-3.5" />
        {experimentName ?? t('header.meta.experiment')}
      </Link>
    ) : null,
  ].filter(Boolean);

  /** Copies a run/workflow id and confirms with a toast — kept off the visible row (lives in `⋯`). */
  const copyId = (value: string, message: string) => {
    copyToClipboard(value);
    toast.success(message);
  };

  /** Sends the pause with the optional reason and closes the dialog once accepted. */
  const confirmPause = () => {
    toggle('pause', pauseReason, {
      onSuccess: () => {
        setPauseOpen(false);
        setPauseReason('');
      },
    });
  };

  /** Archives or unarchives the run; the mutation itself toasts the result and closes the dialog once it settles. */
  const confirmArchive = () => {
    const onSettled = () => {
      setArchiveOpen(false);
    };
    if (archived) unarchive({ onSettled });
    else archive({ onSettled });
  };

  /** The ONE primary action for the run's state (replaces the next-step banner). */
  const primary = (action: RunNextAction | null) => {
    if (!action) return null;
    switch (action.kind) {
      case 'experiment':
        return (
          <Button asChild size="sm" className="cursor-pointer">
            <Link
              to="/catalog-enrichment/experiments/$experimentId"
              params={{ experimentId: action.experimentId }}
              search={crossLinkSearch}
            >
              {t('nextStep.gate.decideInExperiment')}
            </Link>
          </Button>
        );
      case 'review':
        return (
          <Button asChild size="sm" className="cursor-pointer">
            {action.tab === 'overview' ? (
              // The Publish approval is decided in Summary's top band, Approval pane selected.
              <Link
                to="/catalog-enrichment/runs/$runId"
                params={{ runId }}
                search={{ fStage: 'APPROVAL' }}
              >
                {t('nextStep.gate.review')}
                <ArrowRight aria-hidden="true" />
              </Link>
            ) : (
              <Link to="/catalog-enrichment/runs/$runId/gate" params={{ runId }}>
                {t('nextStep.gate.review')}
                <ArrowRight aria-hidden="true" />
              </Link>
            )}
          </Button>
        );
      case 'recovery':
        return (
          <Button asChild size="sm" variant="destructive" className="cursor-pointer">
            <Link to="/catalog-enrichment/runs/$runId/recovery" params={{ runId }}>
              {t('nextStep.failed.recover')}
            </Link>
          </Button>
        );
      case 'delivery':
        return (
          <Button asChild size="sm" variant="outline" className="cursor-pointer">
            {/* "Did it land" lives in Summary's Publish stage pane (Publishing tab folded in). */}
            <Link
              to="/catalog-enrichment/runs/$runId"
              params={{ runId }}
              search={{ fStage: 'DELIVERY' }}
            >
              {t('nextStep.seeDelivery')}
            </Link>
          </Button>
        );
      case 'resume':
        return (
          <Button
            size="sm"
            className="cursor-pointer disabled:cursor-not-allowed"
            onClick={() => {
              toggle('resume');
            }}
            disabled={isPending}
            isLoading={pendingKind === 'resume'}
          >
            <Play aria-hidden="true" />
            {t('header.resume')}
          </Button>
        );
    }
  };
  // The list's status vocabulary (Rejected vs Failed, Delivering…) when the run record has it.
  const badgeStatus = run?.listStatus ?? runStatus;
  // How long the open gate has waited on a person (the band's bold figure).
  const gateWaited = progress?.pendingGate
    ? formatDurationMs(progress.pendingGate.waitingMs)
    : null;

  const help = (
    <HelpPanel trigger="icon" title={t('header.help.title')} description={t('overview.purpose')}>
      <HelpTerms
        terms={[
          { term: tCatalog('phases.PREPARE'), description: t('header.help.prepare') },
          { term: tCatalog('phases.CHECK_QUALITY'), description: t('header.help.checkQuality') },
          { term: tCatalog('phases.GENERATE'), description: t('header.help.generate') },
          { term: tCatalog('phases.SHIP'), description: t('header.help.ship') },
          { term: t('header.help.decisionTerm'), description: t('header.help.decision') },
          { term: t('header.help.unitsTerm'), description: t('header.help.units') },
        ]}
      />
      <FlowStagesHelp
        run={run}
        destination={connector ? catalogLabel(tCatalog, 'connector', connector) : null}
      />
      <DeliveryHelp />
    </HelpPanel>
  );

  // Raw identifiers for the support/debug panel — field names stay verbatim (untranslated),
  // a troubleshooting view, not customer UI. The Temporal link carries the `agentic-workflow`
  // namespace, where the catalog batch pipeline runs (not the integrations default).
  const workflowRef = progress?.workflowId ?? run?.workflowRef ?? null;
  const debugIds: DebugIdEntry[] = [
    { label: 'runId', value: runId },
    {
      label: 'workflowId',
      value: workflowRef,
      href: temporalWorkflowUrl(workflowRef, TEMPORAL_NAMESPACE.workflow),
    },
    { label: 'catalogId', value: run?.catalogId ?? null },
    { label: 'experimentId', value: experimentId },
    { label: 'scheduleId', value: run?.scheduleId ?? null },
    { label: 'parentRunId', value: run?.parentRunId ?? null },
  ];

  return (
    <>
      <header
        className="bg-card flex flex-col overflow-hidden rounded-2xl border"
        data-testid="catalog-enrichment-run-header"
      >
        <div className="flex flex-col gap-3 px-6 pt-5 pb-5">
          <div className="flex items-center justify-between gap-2">
            <PageBreadcrumbs items={breadcrumbs} variant="header" />
            <PageHelpScope>{help}</PageHelpScope>
          </div>

          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                <h1
                  className="text-foreground min-w-0 truncate text-[28px] leading-tight font-semibold"
                  title={title}
                  data-testid="catalog-enrichment-run-title"
                >
                  {title}
                </h1>
                {archived ? (
                  <span className="text-secondary-text text-sm">{t('header.archived')}</span>
                ) : null}
              </div>
              <div className="text-secondary-text flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                {facts.map((fact, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    {i > 0 ? (
                      <span aria-hidden="true" className="text-neutral-80">
                        ·
                      </span>
                    ) : null}
                    {fact}
                  </span>
                ))}
                {/* Raw ids are a support aid, not customer UI (guide part 2 §2.6). */}
                {showDebug ? (
                  <HeaderIdsPopover runId={runId} progress={progress} activeTab={activeTab} />
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {progress ? <RunFactsActions runId={runId} run={run} progress={progress} /> : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-9 cursor-pointer"
                    aria-label={t('header.moreActions')}
                  >
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-w-80">
                  {/* Pause lives here: it is never the run's primary action (Resume is, when paused). */}
                  {live && !paused ? (
                    <>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        disabled={isPending}
                        onClick={() => {
                          setPauseOpen(true);
                        }}
                      >
                        <Pause aria-hidden="true" />
                        {t('header.pause')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => {
                      copyId(runId, t('header.toast.copiedRunId'));
                    }}
                  >
                    <Copy aria-hidden="true" />
                    {t('header.copyRunId')} ({shortId(runId)})
                  </DropdownMenuItem>
                  {workflowId ? (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => {
                        copyId(workflowId, t('header.toast.copiedWorkflowId'));
                      }}
                    >
                      <Copy aria-hidden="true" />
                      {t('header.copyWorkflowId')} ({shortId(workflowId)})
                    </DropdownMenuItem>
                  ) : null}
                  {progress && hasFailure(progress) ? (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link
                        to="/catalog-enrichment/runs/$runId/recovery"
                        params={{ runId }}
                        data-testid="catalog-enrichment-run-menu-retry-options"
                      >
                        <RotateCcw aria-hidden="true" />
                        {t('header.retryOptions')}
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  {run?.parentRunId ? (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link
                        to="/catalog-enrichment/runs/$runId"
                        params={{ runId: run.parentRunId }}
                      >
                        {t('header.parentRun')}: {shortId(run.parentRunId)}
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  {run ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        disabled={isArchivePending}
                        onClick={() => {
                          setArchiveOpen(true);
                        }}
                      >
                        {archived ? (
                          <ArchiveRestore aria-hidden="true" />
                        ) : (
                          <Archive aria-hidden="true" />
                        )}
                        {archived ? t('header.unarchive') : t('header.archive')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  {showDebug ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                          setIsDebugOpen((open) => !open);
                        }}
                      >
                        <Bug aria-hidden="true" />
                        {t('header.supportDebug')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {children}
        </div>

        {/* The status band: the batch's state in its colour, the one sentence on where it stands
            and the ONE primary action for it (Review, Resume, Recover, See delivery…). */}
        {progress || badgeStatus ? (
          <div
            data-testid="catalog-enrichment-run-status-band"
            className={cn(
              'flex flex-wrap items-center gap-x-4 gap-y-2 border-t px-6 py-3',
              bandTone(
                progress?.costGate ? 'COST_GATE' : badgeStatus,
                run ? isRecentRun(run) : true,
              ),
            )}
          >
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
              {run ? (
                <StatusCell
                  run={run}
                  status={progress?.costGate ? 'COST_GATE' : badgeStatus}
                  testId="catalog-enrichment-run-status-badge"
                  plain
                />
              ) : badgeStatus ? (
                <BatchStatusBadge status={badgeStatus} />
              ) : null}
              {progress?.pendingGate ? (
                <span
                  className="text-secondary-text text-sm"
                  data-testid="catalog-enrichment-run-status-line"
                >
                  <span className="text-foreground font-medium">
                    {t('header.band.gate', { stage: stageLabel(progress.pendingGate.stage) })}
                  </span>
                  {gateWaited ? (
                    <>
                      {' · '}
                      <Trans
                        t={t}
                        i18nKey={
                          progress.pendingGate.waitingSince
                            ? 'header.band.waitingSince'
                            : 'header.band.waiting'
                        }
                        values={{
                          waited: gateWaited,
                          since: progress.pendingGate.waitingSince
                            ? formatDateTime(progress.pendingGate.waitingSince)
                            : '',
                        }}
                        components={{ b: <b className="text-foreground font-semibold" /> }}
                      />
                    </>
                  ) : null}
                </span>
              ) : progress ? (
                <RunStateLine
                  runId={runId}
                  run={run}
                  progress={progress}
                  label={next?.label ?? null}
                  hint={next?.hint ?? null}
                />
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <NextWaitingBatchLink runId={runId} />
              {progress && isRunStopped(progress.runStatus) ? (
                <RunStoppedPrimary runId={runId} progress={progress} />
              ) : (
                primary(next?.action ?? null)
              )}
            </div>
          </div>
        ) : null}
      </header>

      <ConfirmDialog
        open={pauseOpen}
        onOpenChange={(open) => {
          if (!isPending) setPauseOpen(open);
        }}
        title={t('header.pauseDialog.title')}
        description={
          <div className="flex flex-col gap-3 text-left">
            <p>{t('header.pauseDialog.body')}</p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="run-pause-reason">{t('header.pauseDialog.reason')}</Label>
              <Textarea
                id="run-pause-reason"
                value={pauseReason}
                onChange={(e) => {
                  setPauseReason(e.target.value);
                }}
                rows={2}
              />
            </div>
          </div>
        }
        confirmLabel={t('header.pauseDialog.confirm')}
        cancelLabel={t('common.cancel')}
        isPending={pendingKind === 'pause'}
        onConfirm={confirmPause}
      />

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (!isArchivePending) setArchiveOpen(open);
        }}
        title={
          archived ? t('header.archiveDialog.unarchiveTitle') : t('header.archiveDialog.title')
        }
        description={
          archived ? t('header.archiveDialog.unarchiveBody') : t('header.archiveDialog.body')
        }
        confirmLabel={archived ? t('header.unarchive') : t('header.archive')}
        cancelLabel={t('common.cancel')}
        isPending={isArchivePending}
        onConfirm={confirmArchive}
      />

      {/* Floating, non-modal debug panel — gated exactly like legacy batch-debug-panel. */}
      {showDebug ? (
        <BatchDebugPanel
          open={isDebugOpen}
          ids={debugIds}
          title={t('header.debugPanel.title')}
          subtitle={t('header.debugPanel.subtitle')}
          onClose={() => {
            setIsDebugOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
